import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useARIA } from '../contexts/ARIAContext';
import GlassCard from './GlassCard';

const ARIACommandTerminal: React.FC = () => {
  const { isOpen, setIsOpen, query, setQuery, history, isThinking, executeCommand } = useARIA();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  // Handle Keyboard Cmd+K global trigger
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    executeCommand(query);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="fixed bottom-28 right-8 w-[400px] max-h-[600px] z-[100]"
        >
          <GlassCard className="h-full flex flex-col p-4 shadow-2xl border-white/20">
            <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-2">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
                <h3 className="text-sm font-bold tracking-wider text-white/90">ARIA COMMAND LAYER</h3>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/40 hover:text-white">✕</button>
            </div>

            {/* Content / Chat History */}
            <div 
              ref={scrollRef}
              className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 custom-scrollbar"
            >
              {history.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center opacity-30 text-center px-8">
                  <span className="text-4xl mb-4">A</span>
                  <p className="text-xs">Sovereign Orchestration Ready.<br/>Query telemetry or dispatch commands.</p>
                </div>
              )}
              {history.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-4 py-2 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-brand-blue/30 border border-brand-blue/20 text-white' 
                      : 'bg-white/5 border border-white/10 text-white/80'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isThinking && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 flex items-center gap-1">
                    <div className="w-1 h-1 bg-white/40 rounded-full animate-bounce" />
                    <div className="w-1 h-1 bg-white/40 rounded-full animate-bounce [animation-delay:0.2s]" />
                    <div className="w-1 h-1 bg-white/40 rounded-full animate-bounce [animation-delay:0.4s]" />
                  </div>
                </div>
              )}
            </div>

            {/* Input Overlay */}
            <form onSubmit={handleSubmit} className="relative">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Examine BTC telemetry..."
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-teal/50 transition-all pr-12"
              />
              <button 
                type="submit"
                disabled={isThinking || !query.trim()}
                className="absolute right-2 top-2 h-8 w-8 rounded-lg bg-brand-teal flex items-center justify-center disabled:opacity-20"
              >
                ↵
              </button>
            </form>
          </GlassCard>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ARIACommandTerminal;
